import { PremiumPackage } from '@project-gd-x/dating-site-contracts/src/gen/gdx/gateway/web/payment/v2/package_pb';
import { computed, reactive, Ref } from 'vue';

import { getCurrencySymbol, getFormattedCurrency, getPriceAlpha2Formatted } from '@/helpers/number';
import { getPackagePriceBreakdown } from '@/pages/payment/helpers/package/package';
import { usePackagePricePerWeek } from '@/pages/payment/hooks/use-package-price/use-package-price';
import { SETTINGS_DEFAULT_CURRENCY } from '@/settings';
import { ExtractClassFields } from '@/type';

type UseSelfArg = {
  selectedModel: Ref<ExtractClassFields<PremiumPackage>>;
  pkg: ExtractClassFields<PremiumPackage>;
  priceDiscountFixed: Ref<bigint>;
  isAlpha2Currency: Ref<boolean>;
};

export function useSelf(arg: UseSelfArg) {
  // Ref -----------------------------------------------------------------------
  const { priceOriginal, priceDiscounted } = getPackagePriceBreakdown(arg.pkg);

  // Hook ----------------------------------------------------------------------
  const packagePricePerWeek = usePackagePricePerWeek(arg.pkg);

  // Computed ------------------------------------------------------------------
  const priceFormatted = computed(() => {
    return arg.isAlpha2Currency.value ? getPriceAlpha2Formatted : getFormattedCurrency;
  });

  const packagePricePerMonthNew = computed(() =>
    priceFormatted.value(
      priceDiscounted.totalAmount ?? 0n,
      priceDiscounted.currency ?? SETTINGS_DEFAULT_CURRENCY,
    ),
  );

  const packagePricePerMonthOld = computed(() => {
    let price = priceOriginal.totalAmount ?? 0n;

    if (arg.priceDiscountFixed.value > 0n) {
      price = arg.priceDiscountFixed.value;
    }

    return priceFormatted.value(price, priceOriginal.currency ?? SETTINGS_DEFAULT_CURRENCY);
  });

  const currencyPrefix = computed(() => {
    const currency = packagePricePerWeek.price.currency || SETTINGS_DEFAULT_CURRENCY;

    if (arg.isAlpha2Currency.value) {
      return currency.slice(0, 2) + getCurrencySymbol(currency);
    }

    return getCurrencySymbol(currency);
  });

  const packagePricePerMonthInt = computed(() => {
    return Math.trunc(Number(priceDiscounted.totalAmount) / 100);
  });

  const packagePricePerMonthCents = computed(() => {
    return Number(priceDiscounted.totalAmount) % 100;
  });

  const packagePricePerDayInt = computed(() => {
    return Math.trunc(Number(packagePricePerWeek.price.totalAmount / 7n) / 100);
  });

  const packagePricePerDayCents = computed(() => {
    return Number(packagePricePerWeek.price.totalAmount / 7n) % 100;
  });

  const packagePricePerDayFormatted = computed(() => {
    return priceFormatted.value(
      packagePricePerWeek.price.totalAmount / 7n,
      packagePricePerWeek.price.currency,
    );
  });

  const isSelected = computed(() => {
    return arg.selectedModel.value.id === arg.pkg.id;
  });

  // Method --------------------------------------------------------------------
  function toggleSelect(): void {
    arg.selectedModel.value = arg.pkg;
  }

  async function init() {}

  return reactive({
    packagePricePerWeek,
    packagePricePerMonthNew,
    packagePricePerMonthOld,
    currencyPrefix,
    packagePricePerMonthInt,
    packagePricePerMonthCents,
    packagePricePerDayInt,
    packagePricePerDayCents,
    packagePricePerDayFormatted,
    isSelected,
    toggleSelect,
    init,
  });
}
